import type { AnalysisType } from '../../../shared/types';
import { analysisTint } from '../../utils/tints';

/* ── Vocabulaire de formes ───────────────────────────────────────────
   Le nuancier compte cinq familles pour neuf typologies : le jaune, le
   rose et le bleu servent chacun deux fois. La silhouette lève l'ambiguïté
   — bouclier contre croix, banderole contre étoile, bulle contre tente.

   Chaque forme est un POLYGONE simple, peint puis recontouré d'un trait
   épais de sa propre couleur, `stroke-linejoin: round`. Les angles
   s'arrondissent tout seuls : aucune courbe de Bézier à écrire ni à
   maintenir, et une silhouette reste lisible jusqu'à ~24 px.

   Ces formes n'habillent PAS les tuiles d'accueil — elles y coûteraient
   la description et le compteur de crédits. Elles servent partout où
   l'icône seule ne suffit pas : bandeau d'analyse, écran d'attente,
   repères d'historique, et côté site (planche, hero, tarifs, bandeau). */

type Spec = { points: string; stroke: number };

/** Étoiles et étincelles : sommets calculés, jamais recopiés à la main. */
function radial(branches: number, outer: number, inner: number, turn = 0): string {
  const pts: string[] = [];
  for (let i = 0; i < branches * 2; i++) {
    const r = i % 2 ? inner : outer;
    const a = (Math.PI * i) / branches - Math.PI / 2 + turn;
    pts.push(`${(50 + r * Math.cos(a)).toFixed(1)},${(50 + r * Math.sin(a)).toFixed(1)}`);
  }
  return pts.join(' ');
}

export const SHAPES = {
  bulle:     { points: '14,20 86,20 86,64 46,64 32,84 32,64 14,64', stroke: 20 },
  bouclier:  { points: '22,22 78,22 78,50 50,84 22,50', stroke: 20 },
  banderole: { points: '16,28 78,28 90,50 78,72 16,72', stroke: 20 },
  blob:      { points: '50,18 72,26 82,48 74,70 52,82 28,74 20,48 30,26', stroke: 30 },
  hexagone:  { points: '50,16 80,33 80,67 50,84 20,67 20,33', stroke: 20 },
  croix:     { points: '38,18 62,18 62,38 82,38 82,62 62,62 62,82 38,82 38,62 18,62 18,38 38,38', stroke: 20 },
  etoile:    { points: radial(8, 38, 17), stroke: 16 },
  tente:     { points: '50,18 84,80 16,80', stroke: 22 },
  etincelle: { points: radial(4, 40, 11, Math.PI / 4), stroke: 16 },
} satisfies Record<string, Spec>;

export type ShapeName = keyof typeof SHAPES;

/** Une silhouette par typologie. Les doublons de couleur sont volontairement
 *  éloignés dans le vocabulaire : deux roses, deux silhouettes opposées. */
const BY_ANALYSIS: Record<AnalysisType, ShapeName> = {
  general_question: 'bulle',
  brand_name: 'bouclier',
  slogan: 'banderole',
  concept: 'blob',
  label: 'hexagone',
  campaign: 'croix',
  social_post: 'etoile',
  event_activation: 'tente',
  creative_brief: 'etincelle',
};

export function shapeFor(type: AnalysisType | string | null | undefined): ShapeName {
  return (type && BY_ANALYSIS[type as AnalysisType]) || 'blob';
}

interface Props {
  /** Typologie d'analyse — détermine silhouette ET couleur. */
  type?: AnalysisType | string | null;
  /** Silhouette imposée, quand elle ne vient pas d'une typologie (décor). */
  shape?: ShapeName;
  /** Couleur imposée. Par défaut : la teinte de la typologie. */
  color?: string;
  className?: string;
}

/**
 * Rend la silhouette en `currentColor` si aucune couleur n'est donnée —
 * ce qui permet de la piloter depuis le parent (filigrane, survol, etc.).
 */
export default function TypologyShape({ type, shape, color, className = 'h-full w-full' }: Props) {
  const spec = SHAPES[shape ?? shapeFor(type)];
  const fill = color ?? (type ? analysisTint(type).hex : 'currentColor');

  return (
    <svg className={className} viewBox="0 0 100 100" aria-hidden="true" focusable="false">
      <polygon
        points={spec.points}
        fill={fill}
        stroke={fill}
        strokeWidth={spec.stroke}
        strokeLinejoin="round"
      />
    </svg>
  );
}
